import React, {useEffect, useMemo, useState} from 'react';
import {Platform} from 'react-native';

import {persistScreenParams} from './persistence.util';

/**
 * Helper to filter serializable parameters.
 * We prioritize route.params. If we must look at props, we are very selective
 * to avoid picking up API response data that might be passed as props.
 */
const getSerializableParams = (params: any, isRouteParams = false) => {
  if (!params || typeof params !== 'object') return null;

  const result: any = {};
  Object.keys(params).forEach(key => {
    const value = params[key];

    // 1. Exclude internal React/Navigation props and navigation-only context parameters
    if (
      key.includes('navigation') ||
      key.includes('route') ||
      key === 'children' ||
      key === 'mainPath' ||
      key === 'modalName' ||
      key === 'orderId'
    ) {
      return;
    }

    // 2. Allow primitives
    if (
      value !== null &&
      value !== undefined &&
      (typeof value === 'string' ||
        typeof value === 'number' ||
        typeof value === 'boolean')
    ) {
      // 3. If these are NOT route params (i.e., they are direct component props),
      // we only take them if they look like identifiers or names to avoid greedy
      // persistence of API data (like volumes or counts).
      if (!isRouteParams) {
        const isIdentifierOrName =
          key.toLowerCase().endsWith('id') ||
          key.toLowerCase().endsWith('id_') ||
          key.toLowerCase().endsWith('name') ||
          [
            'recordId',
            'coachId',
            'clientId',
            'shadowId',
            'VolPerfUserId',
            'VolPerfUserName',
            'isViewTeam',
            'isViewTeamsMode',
            'title',
            'reportHeading',
            'screenContext',
          ].includes(key);

        if (!isIdentifierOrName) return;
      }

      result[key] = value;
    }
    // 4. Explicitly allow modalProps for reports
    else if (
      (key === 'modalProps' || key === 'params') &&
      typeof value === 'object' &&
      value !== null &&
      !Array.isArray(value)
    ) {
      result[key] = value;
    }
  });
  return Object.keys(result).length > 0 ? result : null;
};

/**
 * withScreenPersistence HOC
 */
export const withScreenPersistence = (
  WrappedComponent: React.ComponentType<any>,
  screenName: string,
) => {
  if (Platform.OS !== 'web') {
    return WrappedComponent;
  }

  const PersistenceWrapper = (props: any) => {
    const [recoveredParams, setRecoveredParams] = useState<any>({});
    const [loading, setLoading] = useState(Platform.OS === 'web');

    const effectiveScreenName = props.persistenceKey || screenName;

    // Identify which parameters to persist.
    const paramsToPersist = useMemo(() => {
      // 1. Try route.params first (This is the standard way React Navigation passes params)
      const routeParams = props.route?.params;
      const serializableRouteParams = getSerializableParams(routeParams, true);

      // On web, if route exists but params are missing, we likely need to recover.
      // We return null to signal that we want to RECOVER instead of persisting empty params.
      if (Platform.OS === 'web' && props.route && !serializableRouteParams) {
        return null;
      }

      if (serializableRouteParams) return serializableRouteParams;

      // 2. Fallback to specific props if route.params is missing
      return getSerializableParams(props, false);
    }, [props, effectiveScreenName]);

    // Stable key for effect
    const paramsKey = useMemo(() => {
      try {
        // Only stringify if we have something to avoid unnecessary work
        if (!paramsToPersist) return 'null';
        return JSON.stringify(paramsToPersist);
      } catch (e) {
        return 'error';
      }
    }, [paramsToPersist]);

    useEffect(() => {
      let isMounted = true;
      const initPersistence = async () => {
        try {
          const recovered = await persistScreenParams(
            effectiveScreenName,
            paramsToPersist,
          );
          if (isMounted) {
            if (recovered && typeof recovered === 'object') {
              const cleanRecovered: any = {};
              Object.keys(recovered).forEach(key => {
                if (recovered[key] !== null && recovered[key] !== undefined) {
                  cleanRecovered[key] = recovered[key];
                }
              });
              setRecoveredParams(cleanRecovered);
            }
            setLoading(false);
          }
        } catch (error) {
          if (isMounted) setLoading(false);
        }
      };

      initPersistence();
      return () => {
        isMounted = false;
      };
    }, [paramsKey, effectiveScreenName]);

    // On mobile or if not loading, render immediately
    if (Platform.OS !== 'web' || !loading) {
      // Merge recovered params into props, but don't overwrite valid existing props.
      const mergedParams = {...recoveredParams};
      Object.keys(props).forEach(key => {
        if (props[key] !== undefined && props[key] !== null) {
          delete mergedParams[key];
        }
      });

      return <WrappedComponent {...props} {...mergedParams} />;
    }

    // On web while loading, return null or a simple placeholder
    return null;
  };

  PersistenceWrapper.displayName = `withScreenPersistence(${
    WrappedComponent.displayName || WrappedComponent.name || 'Component'
  })`;

  return PersistenceWrapper;
};
